import * as Sentry from "@sentry/react";
import {
  IS_PRODUCTION,
  SENTRY_DSN,
  SENTRY_TRACE_TARGETS,
  SENTRY_ENVIRONMENT,
  APP_VERSION,
} from "@/config";
import {
  MASK,
  SENSITIVE_FIELD_KEYS,
  maskSensitiveFields,
} from "./mask-sensitive-fields";

// Sentry turns any object nested deeper than this into "[Object]".
const NORMALIZE_DEPTH = 6;

const EXTENSION_ERROR_PATTERNS = [
  "DISCOUNT_CODE",
  "chrome-extension",
  "safari-extension",
  "moz-extension",
  "Non-Error promise rejection captured",
  "Script error.",
  "ResizeObserver loop limit exceeded",
  "Load failed",
  "atomicFindClose",
  "conduitPage",
  "SmallChatCustom",
  "__gCrWeb",
  "fb_xd_fragment",
  "top.GLOBALS",
  "originalCreateNotification",
  "canvas.contentDocument",
  "MyApp_RemoveAllHighlights",
  "uBlock",
  "AdBlock",
  "ghostery",
  // VWO's A/B testing script, injected site-wide, calls window.gtag with an
  // undeclared variable.
  "eventName is not defined",
  // AppLovin SDK running inside in-app browsers.
  "al_onAdViewRendered",
  // Android/iOS in-app webviews tearing down their JS bridge mid-session.
  "Java object is gone",
  "WKWebView API client did not respond",
  "window.webkit.messageHandlers",
  // Password managers reading their own injected globals.
  "DomainData",
  "evaluating 't.Domain'",
];

const EXTENSION_URL_PATTERNS = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "webkit-masked-url://",
  "ms-browser-extension://",
  "activity.wisepops.com",
  "wisepops.com",
  "googleads4.g.doubleclick.net",
  "doubleclick.net",
  // OneTrust cookie banner, served by the host site.
  "otBannerSdk",
  "/scripttemplates/",
  // VWO.
  "visualwebsiteoptimizer",
];

// Function names belonging to scripts injected into the page, which show up as
// `<anonymous>` frames and so can't be identified by their filename.
const INJECTED_FUNCTION_PATTERNS = [
  "logEvaluatedStringInfo",
  "al_onAdViewRendered",
];

// Frames with these filenames say nothing about where the error came from, so
// they're skipped when looking for the frame that actually threw.
const OPAQUE_FRAME_FILENAMES = new Set([
  "[native code]",
  "<anonymous>",
  "native",
]);

// The directory our bundle and its chunks are served from. Derived at runtime
// so it holds for every build mode (embed, static config, query param alias).
function getAppScriptBase(): string | null {
  const url = import.meta.url;

  if (!url.startsWith("http")) {
    return null;
  }

  return url.slice(0, url.lastIndexOf("/") + 1);
}

/**
 * Decides whether an event comes from code we don't ship and can't fix.
 *
 * Errors thrown by third-party scripts injected into msf.org.uk (analytics,
 * cookie banners, password managers, in-app webviews) reach our Sentry because
 * the SDK instruments the whole page, not just our bundle.
 */
export function shouldIgnoreError(
  event: Sentry.ErrorEvent,
  appScriptBase: string | null = getAppScriptBase(),
): boolean {
  const errorMessage = event.exception?.values?.[0]?.value || "";
  const errorType = event.exception?.values?.[0]?.type || "";
  const stacktrace = event.exception?.values?.[0]?.stacktrace?.frames || [];

  const hasExtensionPattern = EXTENSION_ERROR_PATTERNS.some(
    (pattern) => errorMessage.includes(pattern) || errorType.includes(pattern),
  );

  if (hasExtensionPattern) {
    return true;
  }

  const hasExtensionUrl = stacktrace.some((frame) => {
    const filename = frame.filename || "";
    return EXTENSION_URL_PATTERNS.some((pattern) => filename.includes(pattern));
  });

  if (hasExtensionUrl) {
    return true;
  }

  const hasInjectedFunction = stacktrace.some((frame) => {
    const fn = frame.function || "";
    return INJECTED_FUNCTION_PATTERNS.some((pattern) => fn.includes(pattern));
  });

  if (hasInjectedFunction) {
    return true;
  }

  return isThrownOutsideAppBundle(stacktrace, appScriptBase);
}

/**
 * Last line of defence for third-party scripts we haven't seen yet: look at the
 * frame that actually threw and drop the event when it isn't our bundle.
 *
 * Deliberately conservative — an event is kept whenever we can't tell, so a
 * stackless error (many BraintreeErrors) or one that died in native code (React
 * calling removeChild) is never discarded.
 */
function isThrownOutsideAppBundle(
  frames: Sentry.StackFrame[],
  appScriptBase: string | null,
): boolean {
  if (!appScriptBase || frames.length === 0) {
    return false;
  }

  // Sentry orders frames oldest first, so the throwing frame is the last one
  // that names a real file.
  const throwingFrame = [...frames]
    .reverse()
    .find(
      (frame) => frame.filename && !OPAQUE_FRAME_FILENAMES.has(frame.filename),
    );

  if (!throwingFrame?.filename) {
    return false;
  }

  return !throwingFrame.filename.startsWith(appScriptBase);
}

// Contexts written by the SDK itself (browser, OS, device...) carry no donor
// data and are the first thing we look at when triaging, so they are kept as
// they are. Everything else -- including `react` (ErrorBoundary component
// props) and our own `payload` context -- goes through the masking helper.
const SDK_CONTEXT_KEYS = new Set([
  "app",
  "browser",
  "culture",
  "device",
  "os",
  "response",
  "runtime",
  "trace",
]);

const URL_BREADCRUMB_DATA_KEYS = ["url", "from", "to"] as const;

function maskQueryString(queryString: string): string {
  const params = new URLSearchParams(queryString);
  const masked = new URLSearchParams();

  for (const [key, value] of params) {
    masked.append(key, SENSITIVE_FIELD_KEYS.has(key) ? MASK : value);
  }

  return masked.toString();
}

function maskUrl(url: string): string {
  const queryStart = url.indexOf("?");

  if (queryStart === -1) {
    return url;
  }

  const path = url.slice(0, queryStart);
  const query = url.slice(queryStart + 1);
  const hashStart = query.indexOf("#");
  const hash = hashStart === -1 ? "" : query.slice(hashStart);
  const search = hashStart === -1 ? query : query.slice(0, hashStart);

  return `${path}?${maskQueryString(search)}${hash}`;
}

function maskRequest(request: NonNullable<Sentry.ErrorEvent["request"]>) {
  const masked = maskSensitiveFields(request);

  if (typeof masked.url === "string") {
    masked.url = maskUrl(masked.url);
  }

  if (typeof masked.query_string === "string") {
    masked.query_string = maskQueryString(masked.query_string);
  }

  return masked;
}

function maskBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  const masked = maskSensitiveFields(breadcrumb);

  if (masked.data) {
    for (const key of URL_BREADCRUMB_DATA_KEYS) {
      const value = masked.data[key];

      if (typeof value === "string") {
        masked.data[key] = maskUrl(value);
      }
    }
  }

  return masked;
}

export function sanitizeEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const sanitized: Sentry.ErrorEvent = { ...event };

  if (sanitized.extra) {
    sanitized.extra = maskSensitiveFields(sanitized.extra);
  }

  if (sanitized.user) {
    sanitized.user = maskSensitiveFields(sanitized.user);
  }

  if (sanitized.contexts) {
    const contexts: NonNullable<Sentry.ErrorEvent["contexts"]> = {};

    for (const [key, context] of Object.entries(sanitized.contexts)) {
      contexts[key] = SDK_CONTEXT_KEYS.has(key)
        ? context
        : maskSensitiveFields(context);
    }

    sanitized.contexts = contexts;
  }

  if (sanitized.request) {
    sanitized.request = maskRequest(sanitized.request);
  }

  if (sanitized.breadcrumbs) {
    sanitized.breadcrumbs = sanitized.breadcrumbs.map(maskBreadcrumb);
  }

  return sanitized;
}

export function init() {
  if (IS_PRODUCTION) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,
      release: APP_VERSION,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
        }),
      ],
      tracesSampleRate: 1.0,
      tracePropagationTargets: SENTRY_TRACE_TARGETS,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      normalizeDepth: NORMALIZE_DEPTH,
      beforeSend(event) {
        if (shouldIgnoreError(event)) {
          return null;
        }

        return sanitizeEvent(event);
      },
    });
  }
}

const BRAINTREE_SCALAR_KEYS = [
  "name",
  "code",
  "type",
  "message",
  "description",
  "httpStatus",
] as const;

const BRAINTREE_NESTED_KEYS = ["details", "originalError", "error"] as const;

// The context root and the `braintree` context itself use two of Sentry's
// levels; anything picked deeper would only show up as "[Object]".
const BRAINTREE_DETAILS_MAX_DEPTH = NORMALIZE_DEPTH - 2;

// braintree-web refuses to build a BraintreeError without a code and a type.
type BraintreeErrorFields = Error & {
  code: string;
  type: string;
  details?: unknown;
};

/**
 * BraintreeError details nest the raw gateway or Cardinal response, and the 3DS
 * lookup sends the billing address under keys the masking list does not know
 * (`streetAddress`, `postalCode`...). Only error metadata is copied.
 */
function pickBraintreeErrorMetadata(
  value: unknown,
  depth: number,
): Record<string, unknown> | undefined {
  if (value === null || typeof value !== "object") {
    return undefined;
  }

  const source = value as Record<string, unknown>;
  const picked: Record<string, unknown> = {};

  for (const key of BRAINTREE_SCALAR_KEYS) {
    const field = source[key];

    if (typeof field === "string" || typeof field === "number") {
      picked[key] = field;
    }
  }

  if (Array.isArray(source.invalidFieldKeys)) {
    picked.invalidFieldKeys = source.invalidFieldKeys.filter(
      (fieldKey) => typeof fieldKey === "string",
    );
  }

  if (depth < BRAINTREE_DETAILS_MAX_DEPTH) {
    for (const key of BRAINTREE_NESTED_KEYS) {
      const nested = pickBraintreeErrorMetadata(source[key], depth + 1);

      if (nested) {
        picked[key] = nested;
      }
    }
  }

  return Object.keys(picked).length > 0 ? picked : undefined;
}

function braintreeErrorContext(error: Error) {
  if (!(error instanceof Error) || error.name !== "BraintreeError") {
    return undefined;
  }

  const { code, type, details } = error as BraintreeErrorFields;
  const pickedDetails = pickBraintreeErrorMetadata(details, 1);

  return { code, type, ...(pickedDetails && { details: pickedDetails }) };
}

export function trackError(
  error: Error,
  context?: {
    payload?: unknown;
    level?: Sentry.SeverityLevel;
    fingerprint?: string[];
  },
) {
  const maskedPayload =
    context?.payload !== undefined
      ? maskSensitiveFields(context.payload)
      : undefined;

  if (!IS_PRODUCTION) {
    const log = context?.level === "warning" ? console.warn : console.error;

    if (maskedPayload !== undefined) {
      log(error, maskedPayload);
    } else {
      log(error);
    }

    return;
  }

  Sentry.withScope((scope) => {
    if (maskedPayload !== undefined) {
      scope.setContext("payload", maskedPayload as Record<string, unknown>);
    }

    if (context?.level !== undefined) {
      scope.setLevel(context.level);
    }

    if (context?.fingerprint !== undefined) {
      scope.setFingerprint(context.fingerprint);
    }

    const braintree = braintreeErrorContext(error);

    if (braintree) {
      scope.setTag("braintree.code", braintree.code);
      scope.setContext("braintree", braintree);
    }

    Sentry.captureException(error);
  });
}
